import React, { useState, useEffect } from "react";
import { Paper, Tabs } from "@mantine/core";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import WebsiteCustomizationTab from "./WebsiteCustomizationTab";
import WebsiteURLManagement from "./WebsiteURLManagement";
import WebsitePhotos from "./WebsitePhotos";
import WebsitePublishSuccessModal from "./WebsitePublishSuccessModal";
import { useSalon } from "../../../context/SalonContext";
import { useTemplate } from "../../../context/TemplateContext";
import { PreviewDataMetadata } from "../../../types/staticWebGenerator";
import { BusinessInfo } from "../../../types/context";

// Color constants
const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_TAB_ACTIVE = "#1976d2";
const COLOR_TAB_INACTIVE = "#6c757d";

interface WebsiteGeneratorLandingPageProps {
  onGenerateTemplate?: () => void;
  onViewPreview?: () => void;
  onDeploy?: () => void;
}

interface LocationState {
  showPublishSuccess?: boolean;
}

const WebsiteGeneratorLandingPage: React.FC<
  WebsiteGeneratorLandingPageProps
> = () => {
  const { businessInfo } = useSalon() as {
    businessInfo?: BusinessInfo | null;
  };
  const { loadPreviewData } = useTemplate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("customization");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [publishMetadata, setPublishMetadata] =
    useState<PreviewDataMetadata | null>(null);

  // Check for publish success state from navigation
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.showPublishSuccess) {
      setShowSuccessModal(true);
      // Clear the state to prevent showing modal on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Fetch preview data to get updated metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!businessInfo?.id) return;

      try {
        const data = await loadPreviewData(businessInfo.id);
        if (data?.metadata) {
          setPublishMetadata(data.metadata);
        }
      } catch (error) {
        console.error("Error fetching preview metadata:", error);
      }
    };

    fetchMetadata();
  }, [businessInfo?.id, loadPreviewData]);

  // Check for tab parameter in URL
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["customization", "url", "photos"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
  };

  return (
    <Paper
      p="xl"
      style={{
        height: "100%",
        minHeight: "600px",
        backgroundColor: "white",
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab(value || "customization")}
        variant="default"
      >
        <Tabs.List
          style={{
            display: "flex",
            flexDirection: "row",
            borderBottom: `1px solid ${COLOR_BORDER_LIGHT}`,
          }}
        >
          <Tabs.Tab
            value="customization"
            style={{
              fontWeight: "normal",
              color:
                activeTab === "customization"
                  ? COLOR_TAB_ACTIVE
                  : COLOR_TAB_INACTIVE,
              borderBottom:
                activeTab === "customization"
                  ? `2px solid ${COLOR_TAB_ACTIVE}`
                  : "2px solid transparent",
              paddingBottom: "12px",
              marginBottom: "-1px",
            }}
          >
            Customization
          </Tabs.Tab>
          <Tabs.Tab
            value="url"
            style={{
              fontWeight: "normal",
              color:
                activeTab === "url" ? COLOR_TAB_ACTIVE : COLOR_TAB_INACTIVE,
              borderBottom:
                activeTab === "url"
                  ? `2px solid ${COLOR_TAB_ACTIVE}`
                  : "2px solid transparent",
              paddingBottom: "12px",
              marginBottom: "-1px",
            }}
          >
            URL
          </Tabs.Tab>
          <Tabs.Tab
            value="photos"
            style={{
              fontWeight: "normal",
              color:
                activeTab === "photos" ? COLOR_TAB_ACTIVE : COLOR_TAB_INACTIVE,
              borderBottom:
                activeTab === "photos"
                  ? `2px solid ${COLOR_TAB_ACTIVE}`
                  : "2px solid transparent",
              paddingBottom: "12px",
              marginBottom: "-1px",
            }}
          >
            Photos
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="customization" pt="md">
          <WebsiteCustomizationTab />
        </Tabs.Panel>

        <Tabs.Panel value="url" pt="md">
          <WebsiteURLManagement businessInfo={businessInfo} />
        </Tabs.Panel>

        <Tabs.Panel value="photos" pt="md">
          <WebsitePhotos />
        </Tabs.Panel>
      </Tabs>

      {/* Publish Success Modal */}
      <WebsitePublishSuccessModal
        opened={showSuccessModal}
        onClose={handleSuccessModalClose}
      />
    </Paper>
  );
};

export default WebsiteGeneratorLandingPage;
